import React, { useMemo } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Link, Typography } from '@mui/material';

const BASE_SX = {
  '& > *:first-of-type': { mt: 0 },
  '& > *:last-child': { mb: 0 },
  '& p': { m: 0, mb: 0.75 },
  '& ul, & ol': { m: 0, mb: 0.75, pl: 2.25 },
  '& li': { mb: 0.25 },
  '& li > p': { mb: 0.25 },
  '& blockquote': {
    m: 0,
    mb: 0.75,
    pl: 1.25,
    borderLeft: '3px solid',
    borderColor: 'divider',
    color: 'text.secondary',
  },
  '& hr': { my: 1, border: 0, borderTop: '1px solid', borderColor: 'divider' },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    mb: 0.75,
    fontSize: '0.8125rem',
  },
  '& th, & td': {
    border: '1px solid',
    borderColor: 'divider',
    px: 0.75,
    py: 0.5,
    textAlign: 'left',
  },
  '& th': { fontWeight: 600, bgcolor: 'action.hover' },
  '& pre': {
    m: 0,
    mb: 0.75,
    p: 1,
    borderRadius: 1,
    overflow: 'auto',
    bgcolor: 'action.hover',
    fontSize: '0.75rem',
    fontFamily: 'monospace',
  },
  '& code': {
    fontFamily: 'monospace',
    fontSize: '0.8125em',
  },
  '& :not(pre) > code': {
    px: 0.5,
    py: 0.125,
    borderRadius: 0.5,
    bgcolor: 'action.hover',
  },
};

const DOCUMENT_SX = {
  '& p': { mb: 1.25, lineHeight: 1.65 },
  '& ul, & ol': { mb: 1.25, pl: 3 },
  '& li': { mb: 0.5 },
  '& blockquote': {
    mb: 1.5,
    pl: 1.75,
    py: 0.5,
    borderLeft: '4px solid',
    borderColor: 'primary.light',
    bgcolor: 'action.hover',
  },
  '& table': {
    display: 'block',
    width: 'max-content',
    maxWidth: '100%',
    overflowX: 'auto',
    mb: 2,
    fontSize: '0.9rem',
    boxShadow: 1,
    borderRadius: 1,
  },
  '& th, & td': {
    border: '1px solid',
    borderColor: 'divider',
    px: 1.25,
    py: 0.75,
  },
  '& th': { fontWeight: 700, bgcolor: 'grey.100' },
  '& a': { fontWeight: 600 },
  '& pre': { mb: 1.5, p: 1.5, fontSize: '0.85rem' },
};

const REMARK_PLUGINS = [remarkGfm];

function buildComponents() {
  return {
    p: ({ children }) => (
      <Typography variant="body1" component="p" color="text.primary">
        {children}
      </Typography>
    ),
    h1: ({ children }) => (
      <Typography
        variant="h5"
        component="h1"
        color="text.primary"
        sx={{ fontWeight: 700, mb: 1.5, pb: 0.75, borderBottom: 2, borderColor: 'divider' }}
      >
        {children}
      </Typography>
    ),
    h2: ({ children }) => (
      <Typography
        variant="h6"
        component="h2"
        color="text.primary"
        sx={{ fontWeight: 700, mb: 1.25, mt: 2 }}
      >
        {children}
      </Typography>
    ),
    h3: ({ children }) => (
      <Typography
        variant="subtitle1"
        component="h3"
        color="text.primary"
        sx={{ fontWeight: 700, mb: 1, mt: 1.5 }}
      >
        {children}
      </Typography>
    ),
    li: ({ children }) => (
      <Typography variant="body1" component="li" color="text.primary">
        {children}
      </Typography>
    ),
    strong: ({ children }) => (
      <Typography component="strong" variant="body1" color="text.primary" sx={{ fontWeight: 700 }}>
        {children}
      </Typography>
    ),
    em: ({ children }) => (
      <Typography component="em" variant="body1" color="text.primary" sx={{ fontStyle: 'italic' }}>
        {children}
      </Typography>
    ),
    a: ({ href, children }) => (
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        underline="always"
        color="primary.main"
        variant="body1"
        sx={{ fontWeight: 600 }}
      >
        {children}
      </Link>
    ),
  };
}

/** Rulebook-style markdown (GFM tables, bold, code fences). Mirrors admin ChatMarkdown document variant. */
function DocumentMarkdown({ content, sx = {} }) {
  const text = typeof content === 'string' ? content : '';
  const components = useMemo(() => buildComponents(), []);

  if (!text.trim()) return null;

  return (
    <Box sx={{ ...BASE_SX, ...DOCUMENT_SX, ...sx }}>
      <Markdown components={components} remarkPlugins={REMARK_PLUGINS} skipHtml>
        {text}
      </Markdown>
    </Box>
  );
}

export default React.memo(DocumentMarkdown);
